import { createBrowserRouter, Outlet, type RouteObject } from 'react-router';
import { RedirectIfAuthenticated, RequireAuth } from '@/auth';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { LandingLayout } from '@/layouts/LandingLayout';
import { MODULES } from '@/modules/registry';
import { AccountPage } from '@/pages/AccountPage';
import { ActivityPage, AdminPage } from '@/pages/admin/AdminPage';
import { ApprovalsPage } from '@/pages/admin/ApprovalsPage';
import { BillingAdminPage } from '@/pages/admin/BillingAdminPage';
import { InstitutionsPage } from '@/pages/admin/InstitutionsPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { BillingPage } from '@/pages/billing/BillingPage';
import { NewInstitutionPage } from '@/pages/billing/NewInstitutionPage';
import { SimulatedPaymentPage } from '@/pages/billing/SimulatedPaymentPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { SignUpPage } from '@/pages/auth/SignUpPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PlansPage } from '@/pages/plans/PlansPage';
import { GroupDetailPage, GroupsPage } from '@/pages/people/GroupsPage';
import { UsersPage } from '@/pages/people/UsersPage';
import { RequireModule, RequireRole } from '@/platform/guards';
import { SessionProvider } from '@/platform/SessionProvider';

/** Wraps a module's routes so they only render while the module is switched on. */
function moduleRoutes(key: string, routes: RouteObject[]): RouteObject {
  return {
    element: (
      <RequireModule module={key}>
        <Outlet />
      </RequireModule>
    ),
    children: routes,
  };
}

/**
 * Public routes live under <AuthLayout>. Private ones need a session (<RequireAuth>)
 * and the user's profile/modules (<SessionProvider>). Module pages come from
 * `src/modules/registry.ts`.
 */
export const router = createBrowserRouter([
  // Public, signed in or not: the plans and the price calculator.
  { path: '/planos', element: <PlansPage /> },
  // Public: Lousa ENEM for students on their own (plans and the way in).
  {
    path: '/enem/conhecer',
    lazy: async () => ({
      Component: (await import('@/modules/enem/pages/EnemLandingPage')).EnemLandingPage,
    }),
  },
  {
    element: (
      <RedirectIfAuthenticated>
        <LandingLayout />
      </RedirectIfAuthenticated>
    ),
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignUpPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/verificar-email', element: <VerifyEmailPage /> },
      // Simulated mode (no Asaas key): the invoice page in place of Asaas's.
      { path: '/pagamento-simulado/:paymentId', element: <SimulatedPaymentPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <SessionProvider>
          <Outlet />
        </SessionProvider>
      </RequireAuth>
    ),
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, path: '/', element: <HomePage /> },
          { path: '/account', element: <AccountPage /> },
          {
            element: (
              <RequireRole roles={['admin', 'coordinator']}>
                <Outlet />
              </RequireRole>
            ),
            children: [
              { path: '/people', element: <UsersPage /> },
              { path: '/people/groups', element: <GroupsPage /> },
              { path: '/people/groups/:groupId', element: <GroupDetailPage /> },
            ],
          },
          {
            element: (
              <RequireRole roles={['admin', 'coordinator']}>
                <Outlet />
              </RequireRole>
            ),
            children: [{ path: '/admin/approvals', element: <ApprovalsPage /> }],
          },
          {
            element: (
              <RequireRole roles={['coordinator']}>
                <Outlet />
              </RequireRole>
            ),
            children: [{ path: '/assinatura', element: <BillingPage /> }],
          },
          {
            element: (
              <RequireRole roles={['admin']}>
                <Outlet />
              </RequireRole>
            ),
            children: [
              { path: '/admin', element: <AdminPage /> },
              { path: '/admin/activity', element: <ActivityPage /> },
              { path: '/admin/institutions', element: <InstitutionsPage /> },
              { path: '/admin/billing', element: <BillingAdminPage /> },
              {
                path: '/admin/components',
                lazy: async () => ({
                  Component: (await import('@/pages/ComponentsPage')).ComponentsPage,
                }),
              },
            ],
          },
          ...MODULES.map((m) => moduleRoutes(m.key, m.routes)),
        ],
      },
      // A coordinator registering one more institution (newcomers get there from the profile step).
      { path: '/instituicoes/nova', element: <NewInstitutionPage /> },
      // Full-screen module pages (e.g. taking an exam) without the main navigation.
      ...MODULES.filter((m) => m.fullscreenRoutes?.length).map((m) =>
        moduleRoutes(m.key, m.fullscreenRoutes!),
      ),
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
